import { useEffect, useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Drawer from "../../components/Drawer";
import { useApi } from "../../hooks/useApi";
import { useAuth } from "../../contexts/AuthContext";
import {
  listOrgFieldGroups,
  createOrgFieldGroup,
  updateOrgFieldGroup,
  deleteOrgFieldGroup,
  listOrgFields,
  createOrgField,
  updateOrgField,
  deleteOrgField,
} from "../../api/orgKbService";
import { paginatedRows } from "../../utils/pagination";
import { showApiError, showToast } from "../../utils/toast";

export default function OrgFieldsPage() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission("org-kb.edit");
  const canDelete = hasPermission("org-kb.delete");

  const [openGroups, setOpenGroups] = useState({});
  const [revealed, setRevealed] = useState({});
  const [openCreateGroup, setOpenCreateGroup] = useState(false);

  const groups = useApi(() => listOrgFieldGroups(), []);
  const groupRows = paginatedRows(groups.data);

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Org Fields</h1>
          <div className="page-sub">Shared credentials, references, and metadata for the team</div>
        </div>
        {canEdit && (
          <button className="btn btn-primary" onClick={() => setOpenCreateGroup(true)}>
            <Icon name="plus" size={14} /> New field group
          </button>
        )}
      </div>

      <div className="col gap-md">
        {groups.loading ? (
          <LoadingBlock rows={2} height={120} />
        ) : groupRows.length === 0 ? (
          <EmptyState
            icon="lock"
            title="No field groups yet"
            hint={canEdit ? "Add a field group to store shared credentials, links, and notes." : "Nothing here yet."}
          />
        ) : (
          groupRows.map((g) => (
            <FieldGroupCard
              key={g.id}
              group={g}
              open={openGroups[g.id] !== false}
              onToggle={() => setOpenGroups({ ...openGroups, [g.id]: !(openGroups[g.id] !== false) })}
              revealed={revealed}
              setRevealed={setRevealed}
              onChanged={() => groups.reload()}
              canEdit={canEdit}
              canDelete={canDelete}
            />
          ))
        )}
      </div>

      <FieldGroupDrawer
        open={openCreateGroup}
        mode="create"
        onClose={() => setOpenCreateGroup(false)}
        onSaved={() => {
          setOpenCreateGroup(false);
          groups.reload();
        }}
      />
    </>
  );
}

function FieldGroupCard({ group, open, onToggle, revealed, setRevealed, onChanged, canEdit, canDelete }) {
  const fields = useApi(() => listOrgFields(group.id, { per_page: 100 }), [group.id]);
  const fieldRows = paginatedRows(fields.data);
  const [fieldDrawer, setFieldDrawer] = useState(null);
  const [groupEditOpen, setGroupEditOpen] = useState(false);

  const handleDeleteGroup = async () => {
    const fieldCount = group.fields_count ?? fieldRows.length;
    const note = fieldCount > 0
      ? `\n\nThis group has ${fieldCount} field${fieldCount === 1 ? "" : "s"}. The backend will refuse if any are present — delete them first.`
      : "";
    if (!window.confirm(`Delete field group "${group.name}"?${note}`)) return;
    try {
      await deleteOrgFieldGroup(group.id);
      showToast("success", "Field group deleted");
      onChanged?.();
    } catch (err) {
      showApiError(err);
    }
  };

  const handleDeleteField = async (f) => {
    if (!window.confirm(`Delete field "${f.field_name}"?`)) return;
    try {
      await deleteOrgField(f.id);
      showToast("success", "Field deleted");
      fields.reload();
      onChanged?.();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <div className="card">
      <div className="card-head" style={{ cursor: "pointer" }} onClick={onToggle}>
        <div className="row gap-sm" style={{ flex: 1, minWidth: 0 }}>
          <Icon name={open ? "chevron-down" : "chevron-right"} size={14} style={{ color: "var(--ink-500)" }} />
          <div style={{ minWidth: 0 }}>
            <div className="card-title" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {group.name}
            </div>
            <div className="card-sub">{group.fields_count ?? fieldRows.length} fields</div>
          </div>
        </div>
        <div className="row gap-sm" onClick={(e) => e.stopPropagation()}>
          {canEdit && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setFieldDrawer({ mode: "create" })}
            >
              <Icon name="plus" size={12} /> Add field
            </button>
          )}
          {canEdit && (
            <button
              className="icon-btn"
              title="Edit group"
              onClick={() => setGroupEditOpen(true)}
            >
              <Icon name="edit" size={13} />
            </button>
          )}
          {canDelete && (
            <button
              className="icon-btn"
              title="Delete group"
              onClick={handleDeleteGroup}
              style={{ color: "var(--danger)" }}
            >
              <Icon name="trash" size={13} />
            </button>
          )}
        </div>
      </div>
      {open && (
        <div className="card-body" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {fields.loading ? (
            <LoadingBlock rows={2} height={48} />
          ) : fieldRows.length === 0 ? (
            <div style={{ color: "var(--ink-500)", fontSize: 13, gridColumn: "1 / -1" }}>No fields yet.</div>
          ) : (
            fieldRows.map((f) => {
              const isPwd = f.field_type === "password";
              const isLink = f.field_type === "link";
              const isRevealed = revealed[f.id];
              return (
                <div key={f.id} className="field">
                  <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
                    <div className="field-label" style={{ flex: 1, minWidth: 0 }}>
                      {f.field_name}
                      {f.is_required && <span style={{ color: "var(--danger)" }}> *</span>}
                    </div>
                    <div className="row gap-sm" style={{ alignItems: "center" }}>
                      <span className="text-xs t-muted">{f.field_type}</span>
                      {canEdit && (
                        <button
                          type="button"
                          className="icon-btn"
                          title="Edit field"
                          style={{ width: 24, height: 24 }}
                          onClick={() => setFieldDrawer({ mode: "edit", field: f })}
                        >
                          <Icon name="edit" size={12} />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          className="icon-btn"
                          title="Delete field"
                          style={{ width: 24, height: 24, color: "var(--danger)" }}
                          onClick={() => handleDeleteField(f)}
                        >
                          <Icon name="trash" size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div style={{ position: "relative" }}>
                    {isLink && f.value ? (
                      <a
                        className="input"
                        href={normalizeUrl(f.value)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={f.value}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          paddingRight: 36,
                          color: "var(--primary-700)",
                          textDecoration: "none",
                          overflow: "hidden",
                        }}
                      >
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            textDecoration: "underline",
                          }}
                        >
                          {f.value}
                        </span>
                        <Icon name="arrow-right" size={12} />
                      </a>
                    ) : (
                      <input
                        className="input"
                        value={isPwd ? (isRevealed ? f.value || "" : "••••••••") : f.value || ""}
                        readOnly
                        style={isPwd ? { paddingRight: 36, fontFamily: isRevealed ? "var(--font-mono)" : "inherit" } : null}
                      />
                    )}
                    {isPwd && (
                      <button
                        type="button"
                        className="icon-btn"
                        title={isRevealed ? "Hide password" : "Show password"}
                        style={{ position: "absolute", right: 4, top: 3, width: 28, height: 28 }}
                        onClick={() => setRevealed({ ...revealed, [f.id]: !isRevealed })}
                      >
                        <Icon name={isRevealed ? "eye-off" : "eye"} size={14} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      <FieldGroupDrawer
        open={groupEditOpen}
        mode="edit"
        group={group}
        onClose={() => setGroupEditOpen(false)}
        onSaved={() => {
          setGroupEditOpen(false);
          onChanged?.();
        }}
      />

      <FieldDrawer
        open={!!fieldDrawer}
        mode={fieldDrawer?.mode}
        field={fieldDrawer?.field}
        groupId={group.id}
        onClose={() => setFieldDrawer(null)}
        onSaved={() => {
          setFieldDrawer(null);
          fields.reload();
          onChanged?.();
        }}
      />
    </div>
  );
}

function FieldGroupDrawer({ open, mode = "create", group, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(mode === "edit" && group ? group.name : "");
    setErrors({});
  }, [open, mode, group]);

  const submit = async () => {
    setErrors({});
    setSaving(true);
    try {
      if (mode === "edit") {
        await updateOrgFieldGroup(group.id, { name });
        showToast("success", "Field group updated");
      } else {
        await createOrgFieldGroup({ name });
        showToast("success", "Field group created");
      }
      onSaved?.();
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={mode === "edit" ? `Edit ${group?.name || "field group"}` : "New field group"}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || !name}>
            {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Create"}
          </button>
        </>
      }
    >
      <div className="field">
        <label className="field-label">Name</label>
        <input
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="GST portal · Internal tools · Bank"
        />
        {errors.name?.[0] && <div className="field-error">{errors.name[0]}</div>}
      </div>
    </Drawer>
  );
}

function FieldDrawer({ open, mode = "create", field, groupId, onClose, onSaved }) {
  const [form, setForm] = useState({
    field_name: "",
    field_type: "string",
    is_required: false,
    value: "",
    order: 0,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && field) {
      setForm({
        field_name: field.field_name || "",
        field_type: field.field_type || "string",
        is_required: !!field.is_required,
        value: field.value ?? "",
        order: field.order ?? 0,
      });
    } else {
      setForm({ field_name: "", field_type: "string", is_required: false, value: "", order: 0 });
    }
    setErrors({});
    setShowPwd(false);
  }, [open, mode, field]);

  const submit = async () => {
    setErrors({});
    setSaving(true);
    try {
      if (mode === "edit") {
        await updateOrgField(field.id, form);
        showToast("success", "Field updated");
      } else {
        await createOrgField(groupId, form);
        showToast("success", "Field created");
      }
      onSaved?.();
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={mode === "edit" ? `Edit ${field?.field_name || "field"}` : "New field"}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || !form.field_name}>
            {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Create"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">Name</label>
          <input className="input" value={form.field_name} onChange={(e) => setForm({ ...form, field_name: e.target.value })} />
          {errors.field_name?.[0] && <div className="field-error">{errors.field_name[0]}</div>}
        </div>
        <div className="field">
          <label className="field-label">Type</label>
          <select className="select" value={form.field_type} onChange={(e) => setForm({ ...form, field_type: e.target.value })}>
            <option value="string">Text (single line)</option>
            <option value="text">Text (multi-line)</option>
            <option value="integer">Integer</option>
            <option value="boolean">Boolean</option>
            <option value="date">Date</option>
            <option value="password">Password</option>
            <option value="link">Link (URL)</option>
          </select>
        </div>
        <div className="field">
          <label className="field-label">Value</label>
          {form.field_type === "text" ? (
            <textarea className="textarea" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
          ) : form.field_type === "password" ? (
            <div style={{ position: "relative" }}>
              <input
                className="input"
                type={showPwd ? "text" : "password"}
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                style={{ paddingRight: 36, fontFamily: showPwd ? "var(--font-mono)" : "inherit" }}
              />
              <button
                type="button"
                className="icon-btn"
                title={showPwd ? "Hide" : "Show"}
                style={{ position: "absolute", right: 4, top: 3, width: 28, height: 28 }}
                onClick={() => setShowPwd((s) => !s)}
              >
                <Icon name={showPwd ? "eye-off" : "eye"} size={14} />
              </button>
            </div>
          ) : (
            <input
              className="input"
              type="text"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
            />
          )}
        </div>
        <div className="row gap-sm">
          <input
            type="checkbox"
            id="org-req"
            checked={form.is_required}
            onChange={(e) => setForm({ ...form, is_required: e.target.checked })}
          />
          <label htmlFor="org-req" className="field-label" style={{ marginBottom: 0 }}>Required field</label>
        </div>
      </div>
    </Drawer>
  );
}

function normalizeUrl(raw) {
  const v = (raw || "").trim();
  if (!v) return v;
  if (/^(https?:|mailto:|tel:|ftp:)/i.test(v)) return v;
  return "https://" + v.replace(/^\/+/, "");
}
